import { useState, useEffect } from 'react'
import { Box, Stack, Text, Button, Badge, Alert, Loading } from '@/components/lib'
import api from '../../api'

// ---------------------------------------------------------------------------
// The Assessment Center StatelessParticipants API returns one item at a time as
// a quirky XML→JSON tree. All of that parsing lives server-side now (see
// parse_item in api/api/promis.py), so the session payload includes a normalised
// `currentQuestion` — { id, context, stem, options: [{oid, label}] } — that this
// component renders directly. Keeping the parsing in one place (shared by web,
// mobile and the stored snapshot) avoids the three copies drifting apart.
// ---------------------------------------------------------------------------

export default function PromisForm ({ participant, dataType, value, onChange, formOid, beginLabel, introText }) {
  const dataTypeId = dataType?._id
  // When administered as part of a battery, a single DataType drives several
  // form sessions; the formOid keeps each one's stored/resumed session distinct.
  const sessionKey = `promis_session_${dataTypeId}${participant?._id ? `_${participant._id}` : ''}${formOid ? `_${formOid}` : ''}`
  // The session is driven by a capability token (returned at start) — held in
  // sessionStorage alongside the session id so the session survives a reload but
  // is cleared when the tab/browser closes (one-sitting questionnaires).
  const tokenKey = `${sessionKey}_token`

  const [status, setStatus] = useState('idle') // idle | resuming | inProgress | submitting | complete | error
  const [session, setSession] = useState(null)
  const [selectedOid, setSelectedOid] = useState(null)
  const [errorMsg, setErrorMsg] = useState(null)

  // On mount: if a session ID + token is in localStorage try to resume it
  useEffect(() => {
    if (!dataTypeId) return
    const savedId = window.sessionStorage.getItem(sessionKey)
    const savedToken = window.sessionStorage.getItem(tokenKey)
    if (!savedId || !savedToken) return
    setStatus('resuming')
    api.promis.getSession(dataTypeId, savedId, { accessToken: savedToken }, s => {
      setSession(s)
      if (s.status === 'complete') {
        // Standalone forms clear on completion; battery legs (which have a
        // formOid) keep the token so the battery can score with it.
        if (!formOid) {
          window.sessionStorage.removeItem(sessionKey)
          window.sessionStorage.removeItem(tokenKey)
        }
        setStatus('complete')
        onChange && onChange(_completionValue(s))
      } else {
        setStatus('inProgress')
      }
    }, () => {
      // Session gone – clear and start fresh
      window.sessionStorage.removeItem(sessionKey)
      window.sessionStorage.removeItem(tokenKey)
      setStatus('idle')
    })
  }, [])

  function _completionValue (s) {
    return { promisSession: s._id, tScore: s.tScore, stdError: s.stdError, itemCount: s.itemCount }
  }

  function startSession () {
    setStatus('resuming')
    setErrorMsg(null)
    api.promis.startSession(dataTypeId, { participant: participant?._id || undefined, formOid: formOid || undefined }, s => {
      window.sessionStorage.setItem(sessionKey, s._id)
      window.sessionStorage.setItem(tokenKey, s.accessToken)
      setSession(s)
      setStatus('inProgress')
    }, err => {
      setErrorMsg(err.message || 'Failed to start the questionnaire.')
      setStatus('error')
    })
  }

  function _handleSessionAdvance (s) {
    setSession(s)
    setSelectedOid(null)
    if (s.status === 'complete') {
      if (!formOid) {
        window.sessionStorage.removeItem(sessionKey)
        window.sessionStorage.removeItem(tokenKey)
      }
      setStatus('complete')
      onChange && onChange(_completionValue(s))
    } else {
      setStatus('inProgress')
    }
  }

  function submitResponse () {
    const item = session?.currentQuestion
    if (!item || !selectedOid) return
    setStatus('submitting')
    setErrorMsg(null)
    api.promis.submitResponse(dataTypeId, session._id, {
      itemId: item.id,
      responseOid: selectedOid,
      accessToken: session.accessToken
    }, _handleSessionAdvance, err => {
      setErrorMsg(err.message || 'Failed to submit your response.')
      setStatus('inProgress')
    })
  }

  function submitSkip () {
    const item = session?.currentQuestion
    if (!item) return
    setStatus('submitting')
    setErrorMsg(null)
    api.promis.submitResponse(dataTypeId, session._id, {
      itemId: item.id,
      skip: true,
      accessToken: session.accessToken
    }, _handleSessionAdvance, err => {
      setErrorMsg(err.message || 'Failed to skip the question.')
      setStatus('inProgress')
    })
  }

  // ---- Idle ---------------------------------------------------------------
  if (status === 'idle') {
    return (
      <Stack gap={3}>
        <Text fontSize='sm' color='fg.muted'>
          {introText || 'In this section you will be presented with a series of questions one at a time. The assessment adjusts based on your answers and will complete automatically.'}
        </Text>
        <Button colorPalette='blue' onClick={startSession}>{beginLabel || 'Begin questionnaire'}</Button>
      </Stack>
    )
  }

  // ---- Loading / resuming -------------------------------------------------
  if (status === 'resuming') {
    return (
      <Box display='flex' alignItems='center' gap={3} py={4}>
        <Loading />
        <Text fontSize='sm' color='fg.muted'>Loading assessment…</Text>
      </Box>
    )
  }

  // ---- Error --------------------------------------------------------------
  if (status === 'error') {
    return (
      <Stack gap={3}>
        <Alert status='error'>{errorMsg || 'An unexpected error occurred.'}</Alert>
        <Button variant='outline' size='sm' onClick={() => { setStatus('idle'); setErrorMsg(null) }}>
          Try again
        </Button>
      </Stack>
    )
  }

  // ---- Complete -----------------------------------------------------------
  if (status === 'complete') {
    return (
      <Stack gap={3}>
        <Alert status='success' title='Assessment complete'>
          Thank you for completing the questionnaire.
        </Alert>
        <Stack gap={1}>
          {dataType?.promisShowTScore && session?.tScore != null &&
            <Box display='flex' alignItems='center' gap={2}>
              <Text fontSize='sm' fontWeight='medium'>T-Score:</Text>
              <Badge colorPalette='blue' size='lg'>{session.tScore}</Badge>
            </Box>}
          <Text fontSize='xs' color='fg.muted'>
            Completed in {session?.itemCount ?? 0} question{session?.itemCount !== 1 ? 's' : ''}.
          </Text>
        </Stack>
      </Stack>
    )
  }

  // ---- In progress (and submitting) ----------------------------------------
  const item = session?.currentQuestion
  const isSubmitting = status === 'submitting'

  if (!item) {
    return <Alert status='warning'>Unable to load the next question. Please refresh and try again.</Alert>
  }

  return (
    <Stack gap={4}>
      {/* context/stem are HTML preserving AC's intended emphasis (e.g. an
          underlined word). They are sanitised server-side (parse_item) to an
          attribute-less allowlist, so rendering them as HTML here is safe. */}
      <Stack gap={1}>
        {item.context && <Text fontWeight='bold' fontSize='md' dangerouslySetInnerHTML={{ __html: item.context }} />}
        <Text fontWeight='medium' fontSize='md' dangerouslySetInnerHTML={{ __html: item.stem }} />
      </Stack>

      <Stack gap={2}>
        {item.options.map(opt => (
          <Box
            key={opt.oid}
            p={3}
            borderWidth='2px'
            borderColor={selectedOid === opt.oid ? 'primary.500' : 'border.default'}
            rounded='md'
            bg={selectedOid === opt.oid ? 'primary.muted' : 'bg.surface'}
            cursor={isSubmitting ? 'not-allowed' : 'pointer'}
            onClick={() => !isSubmitting && setSelectedOid(opt.oid)}
            transition='all 0.1s'
          >
            <Text fontSize='sm'>{opt.label}</Text>
          </Box>
        ))}
      </Stack>

      {errorMsg && <Alert status='error'>{errorMsg}</Alert>}

      <Button
        colorPalette='blue'
        disabled={!selectedOid || isSubmitting}
        isLoading={isSubmitting}
        onClick={submitResponse}
      >
        Next
      </Button>

      <Button
        variant='ghost'
        size='sm'
        color='fg.muted'
        disabled={isSubmitting}
        onClick={submitSkip}
      >
        Skip this question
      </Button>
    </Stack>
  )
}
